import { coachShortDisplayName } from '@efdigital/ll-addon-coaching';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import {
  queryCoachProfilesByUserId,
  queryCoachProfilesByUserIds,
} from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import type { Store } from '@warp-drive/core';

function preferPublished(profiles: CoachProfile[]): CoachProfile | undefined {
  return (
    profiles.find((profile) => profile.status === 'published') ?? profiles[0]
  );
}

/**
 * Resolves a coach's display name from their CoachProfile rather than the
 * central user directory, which doesn't reliably carry a title/email for
 * coach accounts. Falls back to the directory only if a coach somehow has
 * no profile at all.
 */
export async function resolveCoachName(
  store: Store,
  userLookup: UserLookupService,
  coachUserId: string
): Promise<string> {
  const result = await store.request(queryCoachProfilesByUserId(coachUserId));
  const profile = preferPublished(result.content.data ?? []);
  if (profile) {
    return coachShortDisplayName(profile);
  }
  const user = await userLookup.lookupUser(coachUserId);
  return userDisplayName(user);
}

/** Batch form of {@link resolveCoachName} — one request for many coaches. */
export async function resolveCoachNames(
  store: Store,
  userLookup: UserLookupService,
  coachUserIds: string[]
): Promise<Record<string, string>> {
  const uniqueIds = [...new Set(coachUserIds.filter(Boolean))];
  if (!uniqueIds.length) {
    return {};
  }

  const result = await store.request(queryCoachProfilesByUserIds(uniqueIds));
  const profilesByUser = new Map<string, CoachProfile[]>();
  for (const profile of result.content.data ?? []) {
    const list = profilesByUser.get(profile.user) ?? [];
    list.push(profile);
    profilesByUser.set(profile.user, list);
  }

  const names: Record<string, string> = {};
  const missing: string[] = [];
  for (const id of uniqueIds) {
    const profile = preferPublished(profilesByUser.get(id) ?? []);
    if (profile) {
      names[id] = coachShortDisplayName(profile);
    } else {
      missing.push(id);
    }
  }

  if (missing.length) {
    const users = await userLookup.lookupUsers(missing);
    for (const id of missing) {
      names[id] = userDisplayName(users[id] ?? { id });
    }
  }

  return names;
}
